import React, { useState, useEffect, useCallback, useRef } from 'react';
import { dashboardAPI } from '../services/api';
import '../pages/CurrencyConverter.css';

/* V70.132 — Döviz çevirici ortak çekirdek: FLAGS + autocomplete + kur hook'u
 * hem tam sayfa (pages/CurrencyConverter) hem dashboard widget'ı hem de
 * TopBar modalı (CurrencyConverterMini) buradan beslenir. */

export const FLAGS = {
  USD:'🇺🇸',EUR:'🇪🇺',GBP:'🇬🇧',CHF:'🇨🇭',JPY:'🇯🇵',SAR:'🇸🇦',AUD:'🇦🇺',CAD:'🇨🇦',
  DKK:'🇩🇰',NOK:'🇳🇴',SEK:'🇸🇪',KWD:'🇰🇼',AED:'🇦🇪',CNY:'🇨🇳',RUB:'🇷🇺',
  BGN:'🇧🇬',RON:'🇷🇴',IRR:'🇮🇷',IQD:'🇮🇶',PKR:'🇵🇰',QAR:'🇶🇦',KRW:'🇰🇷',
  AZN:'🇦🇿',GEL:'🇬🇪',XDR:'🏳️',BHD:'🇧🇭',LYD:'🇱🇾',JOD:'🇯🇴',EGP:'🇪🇬',
  TRY:'🇹🇷',
  INR:'🇮🇳',BRL:'🇧🇷',ZAR:'🇿🇦',MXN:'🇲🇽',THB:'🇹🇭',MYR:'🇲🇾',IDR:'🇮🇩',
  PHP:'🇵🇭',SGD:'🇸🇬',HKD:'🇭🇰',NZD:'🇳🇿',TWD:'🇹🇼',CLP:'🇨🇱',COP:'🇨🇴',
  PEN:'🇵🇪',ARS:'🇦🇷',CZK:'🇨🇿',PLN:'🇵🇱',HUF:'🇭🇺',HRK:'🇭🇷',ISK:'🇮🇸',
  NGN:'🇳🇬',KES:'🇰🇪',GHS:'🇬🇭',TZS:'🇹🇿',UGX:'🇺🇬',MAD:'🇲🇦',TND:'🇹🇳',
  DZD:'🇩🇿',OMR:'🇴🇲',UAH:'🇺🇦',KZT:'🇰🇿',UZS:'🇺🇿',VND:'🇻🇳',MMK:'🇲🇲',
  BDT:'🇧🇩',LKR:'🇱🇰',NPR:'🇳🇵',AFN:'🇦🇫',XAF:'🌍',XOF:'🌍',ETB:'🇪🇹',
  RSD:'🇷🇸',ALL:'🇦🇱',MKD:'🇲🇰',BAM:'🇧🇦',MDL:'🇲🇩',AMD:'🇦🇲',
  BYN:'🇧🇾',TMT:'🇹🇲',KGS:'🇰🇬',TJS:'🇹🇯',MNT:'🇲🇳',
};

/* ── Autocomplete Dropdown ── */
export const CurrencyAutocomplete = ({ value, onChange, options, getName }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = options.filter(c => {
    if (!query) return true;
    const s = query.toLowerCase();
    return c.toLowerCase().includes(s) || getName(c).toLowerCase().includes(s);
  });

  const select = (code) => {
    onChange(code);
    setQuery('');
    setOpen(false);
  };

  const displayLabel = `${FLAGS[value] || ''} ${value} – ${getName(value)}`;

  return (
    <div className="cc-autocomplete" ref={wrapRef}>
      <div
        className="cc-ac-trigger"
        onClick={() => { setOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
      >
        {displayLabel}
        <span className="cc-ac-arrow">▾</span>
      </div>
      {open && (
        <div className="cc-ac-dropdown">
          <input
            ref={inputRef}
            type="text"
            className="cc-ac-input"
            placeholder="Ara... (kod veya isim)"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
              if (e.key === 'Enter' && filtered.length > 0) select(filtered[0]);
            }}
          />
          <div className="cc-ac-list">
            {filtered.map(c => (
              <div
                key={c}
                className={`cc-ac-item${c === value ? ' cc-ac-active' : ''}`}
                onClick={() => select(c)}
              >
                <span className="cc-flag">{FLAGS[c] || '💱'}</span>
                <strong>{c}</strong>
                <span className="cc-ac-name">{getName(c)}</span>
              </div>
            ))}
            {filtered.length === 0 && (
              <div className="cc-ac-empty">Sonuç bulunamadı</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

/* ── Kur verisi + çevirme mantığı (TCMB + çapraz kur) ── */
export const useCurrencyRates = () => {
  const [rates, setRates] = useState([]);
  const [gfRates, setGfRates] = useState([]);
  const [date, setDate] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchRates = useCallback(async () => {
    try {
      setLoading(true);
      const [tcmbRes, gfRes] = await Promise.all([
        dashboardAPI.getTcmbRates(),
        dashboardAPI.getGfRates().catch(() => ({ data: { rates: [] } })),
      ]);
      const tcmbRates = tcmbRes.data.rates || [];
      setRates(tcmbRates);
      setDate(tcmbRes.data.date || '');

      const tcmbCodes = new Set(tcmbRates.map(r => r.code));
      tcmbCodes.add('TRY');
      const filtered = (gfRes.data.rates || []).filter(r => !tcmbCodes.has(r.code));
      setGfRates(filtered);
    } catch (err) {
      console.error('Kur verisi alınamadı:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const rateMap = {};
  rates.forEach(r => { rateMap[r.code] = r; });
  const gfMap = {};
  gfRates.forEach(r => { gfMap[r.code] = r; });

  const allCodes = ['TRY', ...rates.map(r => r.code), ...gfRates.map(r => r.code)];

  const getRate = (code, type) => {
    if (code === 'TRY') return 1;
    const r = rateMap[code];
    if (r) return type === 'buy' ? r.buy : r.sell;
    const g = gfMap[code];
    if (g) return g.try_rate;
    return null;
  };

  const convert = (amt, fromCode, toCode, type) => {
    const fromRate = getRate(fromCode, type);
    const toRate = getRate(toCode, type === 'buy' ? 'sell' : 'buy');
    if (!fromRate || !toRate) return null;
    const tryAmount = fromCode === 'TRY' ? amt : amt * fromRate;
    return toCode === 'TRY' ? tryAmount : tryAmount / toRate;
  };

  const getName = (code) => {
    if (code === 'TRY') return 'Türk Lirası';
    if (rateMap[code]?.name_tr) return rateMap[code].name_tr;
    if (gfMap[code]?.name) return gfMap[code].name;
    return code;
  };

  return { rates, gfRates, date, loading, rateMap, gfMap, allCodes, getRate, convert, getName };
};

/* ── Kompakt çevirici (dashboard widget + header modal) ── */
const CurrencyConverterMini = ({ variant = 'widget' }) => {
  const { date, loading, rateMap, gfMap, allCodes, convert, getName } = useCurrencyRates();
  const [amount, setAmount] = useState('1');
  const [from, setFrom] = useState('USD');
  const [to, setTo] = useState('TRY');

  const numAmount = parseFloat(amount) || 0;
  const resultBuy = convert(numAmount, from, to, 'buy');
  const resultSell = convert(numAmount, from, to, 'sell');
  const isGfFrom = !rateMap[from] && from !== 'TRY' && gfMap[from];
  const isGfTo = !rateMap[to] && to !== 'TRY' && gfMap[to];
  const singleRate = isGfFrom || isGfTo;

  const fmt = (v) => v.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });

  if (loading) return <div className="cc-mini-loading">Kurlar yükleniyor...</div>;

  return (
    <div className={`cc-mini cc-mini-${variant}`}>
      <div className="cc-mini-row">
        <div className="cc-field">
          <label>Tutar</label>
          <input
            type="number"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            className="cc-input"
            min="0"
            step="any"
          />
        </div>
      </div>
      <div className="cc-mini-row">
        <div className="cc-field">
          <label>Kaynak</label>
          <CurrencyAutocomplete value={from} onChange={setFrom} options={allCodes} getName={getName} />
        </div>
        <button className="cc-swap" onClick={() => { setFrom(to); setTo(from); }} title="Değiştir">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 3 4 7l4 4"/><path d="M4 7h16"/><path d="m16 21 4-4-4-4"/><path d="M20 17H4"/></svg>
        </button>
        <div className="cc-field">
          <label>Hedef</label>
          <CurrencyAutocomplete value={to} onChange={setTo} options={allCodes} getName={getName} />
        </div>
      </div>
      {numAmount > 0 && resultBuy !== null && (
        <div className="cc-mini-results">
          {singleRate ? (
            <div className="cc-mini-result">
              <span className="cc-mini-result-label">Çapraz</span>
              <span className="cc-mini-result-value">{fmt(resultBuy)} {to}</span>
            </div>
          ) : (
            <>
              <div className="cc-mini-result">
                <span className="cc-mini-result-label">Alış</span>
                <span className="cc-mini-result-value">{fmt(resultBuy)} {to}</span>
              </div>
              {resultSell !== null && (
                <div className="cc-mini-result">
                  <span className="cc-mini-result-label">Satış</span>
                  <span className="cc-mini-result-value">{fmt(resultSell)} {to}</span>
                </div>
              )}
            </>
          )}
        </div>
      )}
      {date && <div className="cc-mini-date">TCMB Kuru: {date}</div>}
    </div>
  );
};

export default CurrencyConverterMini;
